<script setup lang="ts">
import eventApi from '@/services/event.service'
import { EStoreModules } from '@/store/storeType'
import {
    defaultEventHubProvider,
    EHubProvider,
    type EventHubProvider
} from '@/types/enum/HubProviderEnum'
import type { LocalStorageFilter } from '@/types/filters/DeviceFilters'
import type { AdvancedEventFilters, EventQueryFilters } from '@/types/filters/EventFilters'
import type { EventModel } from '@/types/models/EventModel'
import { AutocompleteField } from '@rfc/rfc-components-library'
import { EFiltersActions } from '@rfc/rfc-components-library/store'
import { ELocalStorage, EProviders, ESortBy } from '@rfc/rfc-components-library/types'
import { EDebounceTime, getItemFromStorage, getSortByKey } from '@rfc/rfc-components-library/utils'
import { useDebounceFn } from '@vueuse/core/index.mjs'
import { inject, nextTick, onMounted, type Ref, ref, watch } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const { handleChangeEvent } = inject<EventHubProvider>(EHubProvider.EVENT, defaultEventHubProvider)
const { query } = inject<{ query?: Ref }>(EProviders.DETECTION, {
    query: ref(undefined)
})

type Props = {
    event?: Partial<EventModel>
    keepEventInStorage?: boolean
    filterAllEvents?: boolean
    filters?: AdvancedEventFilters & EventQueryFilters
}
const props = defineProps<Props>()
type Emits = {
    (e: 'change', value?: EventModel): void
}
const emit = defineEmits<Emits>()

const handleChange = async (value?: string) => {
    if (value === '') return
    await nextTick(() => {
        if (value === props.event?.name) return
        loadingEvents.value = true
        debouncedQuestion(value)
    })
}

const loadingEvents = ref(false)
const debouncedQuestion = useDebounceFn((value?: string) => getEvents(value), EDebounceTime.DEFAULT)
const getEvents = (name?: string) => {
    eventApi
        .getEvents(
            {
                filters: {
                    name: name ? name : undefined,
                    ...props.filters,
                    ...(props.filterAllEvents ? {} : { ongoing: 1, upcoming: 1 })
                },
                sort: getSortByKey(ESortBy.DATE_RECENT)
            },
            true
        )
        .then(res => {
            const currentEvent = getItemFromStorage<LocalStorageFilter>(
                ELocalStorage.FILTERS
            )?.event
            eventOptions.value =
                currentEvent && currentEvent.id
                    ? [
                          { ...currentEvent, id: currentEvent.id },
                          ...(res.data?.filter(item => item.id !== currentEvent?.id) ?? [])
                      ]
                    : (res.data ?? [])
            if (props.event && !eventOptions.value.find(item => item.id === props.event?.id))
                eventOptions.value = [{ ...props.event, id: props.event.id }, ...eventOptions.value]
        })
        .finally(() => (loadingEvents.value = false))
}

const eventOptions = ref<Partial<EventModel>[]>([])

const handleSelect = async (event?: EventModel) => {
    if (props.keepEventInStorage)
        store?.dispatch(
            `${EStoreModules.FILTERS}/${EFiltersActions.SET_FILTER}`,
            event ? { event: { name: event.name, id: event.id }, checkpoint: undefined } : undefined
        )
    emit('change', event)
}
onMounted(() => getEvents())

watch(
    () => query?.value?.event_id,
    newEvent => {
        if (
            newEvent?.id &&
            !eventOptions.value.find(item => item.id === parseInt(newEvent.id!.toString()))
        )
            eventOptions.value = [
                { ...newEvent, id: parseInt(newEvent.id.toString()) },
                ...eventOptions.value
            ]
        handleChangeEvent?.(
            newEvent?.id ? { ...newEvent, id: parseInt(newEvent.id?.toString()) } : undefined
        )
    }
)
</script>

<template>
    <AutocompleteField
        :label="$t('device.filters.event')"
        :placeholder="$t('common.typeYourSearch')"
        :modelValue="event"
        @typing="handleChange($event)"
        @select="handleSelect($event as EventModel)"
        labelKey="name"
        valueKey="id"
        :options="eventOptions" />
</template>
